// РАСКЛАДКИ КАРТОЧЕК КОМПАНИИ И КОНТАКТА/СУДНА — ПО ЖИВОМУ BITRIX.
//
// Откуда раскладки. Сняты из DOM боевого Bitrix (cp.cir.cx) 31.07.2026: компания 20205,
// контакт 1504 и судно 20681. У контакта и судна макет ОДИН — Bitrix различает их только
// данными, и раскладка здесь одна на двоих. Заголовки разделов — ДОСЛОВНО как в Bitrix,
// включая строчные буквы и опечатку «основаня»: карточка обещает «тот же вид», и молча
// исправлять чужой заголовок значило бы соврать про источник (текст правится в Bitrix).
//
// Подписи полей. В bx_fields колонка title у UF-полей пуста (равна коду): человеческое
// название Bitrix кладёт в raw->>'formLabel', а /fields ядра его пока не отдаёт. Поэтому
// у каждого поля записана ЗАПАСНАЯ подпись — formLabel из bx_fields, снятый 31.07.2026.
// Боевое название всегда главнее: карточки берут f.titleOr(code, label), то есть если
// /fields научится отдавать formLabel (см. отчёт), переименование в Bitrix доедет само.
//
// Откуда значения. Умолчание CRM — своя база (/api/own/*), а она отдаёт карточку БЕЗ
// сырых UF-полей (crm_own_api.py, фиксированный набор ключей). Полные значения лежат в
// зеркале: crm.company.get/crm.contact.get отдают raw целиком (366 и 1010 ключей —
// проверено вызовом ядра 31.07.2026). Поэтому здесь есть useСырьёКарточкиBitrix — второй
// запрос за сырьём зеркала, и слитьДляРаскладки — как совместить его с ответом своей базы.
//
// Ловушки данных Bitrix, закреплённые здесь (все видны в живых ответах):
//   • пустота пишется по-разному: null, '', false, [] — а у ссылочных полей ещё и '0'
//     («нет связи», а не запись №0);
//   • деньги — строкой '515000|RUB';
//   • перечисления приходят ID (и множественные — массивом ID: [2489]);
//   • телефоны/почты — массив {VALUE, VALUE_TYPE};
//   • файлы — объект {id, showUrl} или массив таких: файл лежит в Bitrix, а не у нас.

import { useQuery } from '@tanstack/react-query'

import { Unauthorized } from './client'
import { источник } from './istochnik'

// ─────────────────────────── форма раскладки ───────────────────────────

/** Типы полей Bitrix, которые встречаются в этих двух раскладках (bx_fields.field_type). */
export type ТипПоля =
  | 'string' | 'double' | 'money' | 'date' | 'datetime' | 'boolean' | 'url'
  | 'enumeration' | 'crm_status' | 'crm' | 'employee' | 'user'
  | 'file' | 'crm_multifield'
  /** Не поле данных, а строка-виджет карточки (REQUISITES, CONTACT, ID): собирает страница. */
  | 'special'

export interface ПолеМакета {
  code: string
  /** Запасная подпись (formLabel из bx_fields, 31.07.2026). Боевая — из /fields, titleOr. */
  label: string
  type: ТипПоля
  multiple?: boolean
  /** Для type='crm': на какую сущность CRM ссылается голый числовой id. */
  crmTarget?: 'company' | 'contact' | 'deal'
  /** Пояснение под значением (например, «запись смарт-процесса Bitrix»). */
  hint?: string
}

export interface РазделМакета {
  key: string
  /** Заголовок — дословно из DOM Bitrix (регистр и опечатки сохранены сознательно). */
  title: string
  поля: ПолеМакета[]
}

const п = (
  code: string, label: string, type: ТипПоля, ещё: Partial<ПолеМакета> = {},
): ПолеМакета => ({ code, label, type, ...ещё })

// ─────────────────────────── компания: 5 разделов ───────────────────────────

export const МАКЕТ_КОМПАНИИ: РазделМакета[] = [
  {
    key: 'основное',
    // Да, в Bitrix так и написано — «основаня». Заголовок зеркальный, правится там.
    title: 'основаня информация о компании',
    поля: [
      п('TITLE', 'Название компании', 'string'),
      п('COMPANY_TYPE', 'Тип компании', 'crm_status'),
      п('UF_CRM_1774252263025', 'Почтовый адрес для отправки документов', 'string',
        { multiple: true }),
      п('UF_CRM_1677571219', 'Регион (из какого региона данный клиент)', 'enumeration',
        { multiple: true }),
      п('UF_CRM_1677498127', 'Головная компания (только для ГК)', 'crm',
        { crmTarget: 'company' }),
      п('EMAIL', 'E-mail', 'crm_multifield', { multiple: true }),
      п('PHONE', 'Телефон', 'crm_multifield', { multiple: true }),
      п('ADDRESS', 'Фактический адрес', 'string'),
      п('REQUISITES', 'Реквизиты', 'special'),
      п('CONTACT', 'Контакты', 'special'),
      п('ID', 'ID', 'special'),
      п('INDUSTRY', 'Сфера деятельности', 'crm_status'),
      п('UF_CRM_588A5B9B0B4CD', 'Юридический адрес', 'string'),
    ],
  },
  {
    key: 'дополнительно',
    title: 'дополнительная информация',
    поля: [
      п('UF_CRM_1679558622', 'Ответственный менеджер ОП', 'employee'),
      п('UF_CRM_1679559486', 'Ответственный координатор', 'employee', { multiple: true }),
      п('ASSIGNED_BY_ID', 'Ответственный', 'user'),
      п('UF_CRM_1722433350', 'Дата договора БТО', 'datetime'),
      п('UF_CRM_1687518231', 'Контрагент ЭДО', 'string'),
      п('UF_CRM_1766669461779', 'Провайдер ЭДО', 'string', { multiple: true }),
      п('UF_CRM_1721746404', 'Договор БТО', 'file', { multiple: true }),
      п('UF_CRM_1663581927', 'Договор в реестре', 'string', { multiple: true }),
      // «Холдинг» ссылается на смарт-процесс 1236 — карточек этого типа в CRM нет,
      // поэтому цели у ссылки нет и значение показывается номером записи.
      п('UF_CRM_1768987233', 'Холдинг', 'crm',
        { hint: 'ссылка на запись смарт-процесса 1236 в Bitrix' }),
      п('UF_CRM_1768827813', '123КПП321', 'string'),
      п('UF_CRM_1677500316', '123ИНН321', 'string'),
      п('UF_CRM_1642774129', 'ФЛОТ', 'string'),
      п('UF_CRM_1652283099422', 'Цвет', 'enumeration'),
    ],
  },
  {
    key: 'контур-фокус',
    title: 'контур. фокус (проверка контрагента)',
    поля: [
      п('UF_CRM_1772457240542', 'Светофор', 'string'),
      п('UF_CRM_1772457665269', 'Чистая прибыль на начало отчетного периода', 'string'),
      п('UF_CRM_1772457753355', 'Выручка на начало отчетного периода', 'string'),
      п('UF_CRM_1772457764595', 'Баланс на начало отчетного периода', 'string'),
      п('UF_CRM_1772458093844', 'Признаки банкротства (текущая стадия, если есть)', 'string'),
      п('UF_CRM_1772458153700',
        'Признаки банкротства (обнаружены сообщения о намерении податься на банкротство)',
        'string'),
      // UF_CRM_COMPANY_IPG_KF_BTN в раскладку НЕ вошло: это кнопка приложения
      // «Контур.Фокус» (тип rest_362_ipg_kf_btn), данных в ней нет.
    ],
  },
  {
    key: 'арбитраж',
    title: 'оценка арбитражных дел и судов общей юрисдикции',
    поля: [
      п('UF_CRM_1772610574535',
        'Дела в качестве ответчика. Количество арбитражных дел за 3 года', 'string'),
      п('UF_CRM_1772606257663',
        'Дела в качестве ответчика. Исковая сумма арбитражных дел за 3 года', 'string'),
      п('UF_CRM_1772608984548',
        'Дела в качестве истца. Количество арбитражных дел за 3 года', 'string'),
      п('UF_CRM_1772609037659',
        'Дела в качестве истца. Исковая сумма арбитражных дел за 3 года', 'string'),
    ],
  },
  {
    key: 'оквэд',
    title: 'оквэд',
    поля: [
      п('UF_CRM_1772457864772', 'ОКВЭД ЮР.ЛИЦО', 'string'),
      п('UF_CRM_1772604941415', 'Основной вид деятельности (название для юр.лица)', 'string'),
      п('UF_CRM_1772457883651', 'ОКВЭД ИП', 'string'),
      п('UF_CRM_1772604997200', 'Основной вид деятельности (название для ИП)', 'string'),
    ],
  },
]

// ─────────────────────── контакт/судно: 5 разделов, макет один ───────────────────────

/**
 * В DOM Bitrix первый раздел содержит EMAIL и PHONE дважды (виджет связи и поле) —
 * здесь по одному разу: две одинаковые строки на экране были бы копией бага вёрстки,
 * а не «точным видом». COMPANY из DOM — это поле COMPANY_ID ответа REST.
 */
export const МАКЕТ_КОНТАКТА: РазделМакета[] = [
  {
    key: 'о-контакте',
    title: 'О контакте',
    поля: [
      п('EMAIL', 'E-mail', 'crm_multifield', { multiple: true }),
      п('PHONE', 'Телефон', 'crm_multifield', { multiple: true }),
      п('COMPANY_ID', 'Компания', 'special'),
      п('ADDRESS', 'Адрес', 'string'),
      п('REQUISITES', 'Реквизиты', 'special'),
      п('ASSIGNED_BY_ID', 'Ответственный', 'user'),
      п('UF_CRM_1508500822', 'IMO', 'double'),
      п('UF_CRM_1545371205405', 'MMSI', 'string'),
      п('LAST_NAME', 'Фамилия', 'string'),
      п('NAME', 'Имя', 'string'),
      п('UF_CRM_1548748850872', 'Форма БТО', 'file', { multiple: true }),
      п('TYPE_ID', 'Тип контакта', 'crm_status'),
      п('UF_CRM_1675839888', 'Исполнитель по верификации', 'employee'),
    ],
  },
  {
    key: 'верификации',
    title: 'Верификации',
    поля: [
      п('UF_CRM_1647928200', 'Короткая Верификация (автозаполнение)', 'file'),
      п('UF_CRM_1653299377437', 'Отчет о сроках годности АКБ на судне (файл)', 'file'),
      п('UF_CRM_1639466383', 'Дата последней Верификации', 'date'),
      п('UF_CRM_1684837797', 'Старт верификации в приложении', 'date'),
      п('UF_CRM_1680777940', 'Сделка (ID для приложения)', 'string'),
      п('UF_CRM_1680777973', 'Проверит Верификацию (для приложения)', 'employee'),
      п('UF_CRM_1544601001555', 'Почему не удалось сделать верификацию?', 'string'),
      п('UF_CRM_1701266614214', 'Рыбная верификация (файл)', 'file'),
      п('UF_CRM_1701375719882', 'Тех поле рыбки', 'string'),
    ],
  },
  {
    key: 'короткая-верификация',
    title: 'короткая верификация',
    поля: [
      п('UF_CRM_1561469575', '5.1 Тип портативной УКВ №1', 'string'),
      п('UF_CRM_1561471404', '5.1 Срок годности батареи УКВ №1', 'date'),
      п('UF_CRM_1561469583', '5.1 Тип портативной УКВ №2', 'string'),
      п('UF_CRM_1561471417', '5.1 Срок годности батареи УКВ №2', 'date'),
      п('UF_CRM_1561469591', '5.1 Тип портативной УКВ №3', 'string'),
      п('UF_CRM_1561471430', '5.1 Срок годности батареи УКВ №3', 'date'),
      п('UF_CRM_1561473210', '5.2 Тип РЛО №1', 'string'),
      п('UF_CRM_1561473271', '5.2 Срок годности батареи РЛО №1', 'date'),
      п('UF_CRM_1561473218', '5.2 Тип РЛО №2', 'string'),
      п('UF_CRM_1561473287', '5.2 Срок годности батареи РЛО №2', 'date'),
      п('UF_CRM_1562846241', 'Тип АРБ', 'string'),
      п('UF_CRM_1573137098', 'Элемент питания АРБ (new дата истечения)', 'date'),
      п('UF_CRM_1573137128', 'Гидростат АРБ (new дата истечения)', 'date'),
      п('UF_CRM_1562848978', 'Тип второго АРБ', 'string'),
      п('UF_CRM_1573137275', 'Дата истечения срока годности батареи второго АРБ (new)', 'date'),
      п('UF_CRM_1573137256', 'Дата истечения срока годности гидростата второго АРБ (new)', 'date'),
      п('UF_CRM_1576846736', 'Модель черного ящика', 'string'),
      п('UF_CRM_1576846841', 'Срок годности гидроакустического датчика VDR', 'date'),
      п('UF_CRM_1576846960', 'Срок годности БАТАРЕЙКИ гидроакустического датчика VDR', 'date'),
      п('UF_CRM_1576847044', 'Срок годности гидростата всплывающей капсулы VDR', 'date'),
      п('UF_CRM_1576847126', 'Срок годности элемента питания всплывающей капсулы VDR', 'date'),
      п('UF_CRM_1576847151', 'Срок годности резервного источника питания VDR', 'date'),
      п('UF_CRM_1585572776', 'Гирокомпас(1) Модель', 'string'),
      п('UF_CRM_1585572788', 'Гирокомпас(2) Модель', 'string'),
      п('UF_CRM_1585572967', 'Гирокомпас(1) Дата замены ПЖ', 'date'),
      п('UF_CRM_1585573015', 'Гирокомпас(2) Дата замены ПЖ', 'date'),
      п('UF_CRM_1585573059', 'Гирокомпас(1) Дата установки ЧЭ', 'date'),
      п('UF_CRM_1585573078', 'Гирокомпас(2) Дата установки ЧЭ', 'date'),
      п('UF_CRM_1585571736', 'РЛС 1 (Модель и диапазон)', 'string'),
      п('UF_CRM_1585571750', 'РЛС 2 (Модель и диапазон)', 'string'),
      п('UF_CRM_1585571823', 'РЛС 1 Время наработки ТХ/STBY (часов)', 'string'),
      п('UF_CRM_1585571836', 'РЛС 2 Время наработки ТХ/STBY (часов)', 'string'),
      п('UF_CRM_1647874921', 'РЛС 1 Дата последней замены магнетрона', 'date'),
      п('UF_CRM_1647874943', 'РЛС 2 Дата последней замены магнетрона', 'date'),
      п('UF_CRM_1647875430', 'РадиоСюрвей (Дата выполнения)', 'date'),
      п('UF_CRM_1647875455', 'РадиоСюрвей (Кто выполнял)', 'string'),
      п('UF_CRM_1647875475', 'РадиоСюрвей (Место выполнения)', 'string'),
      п('UF_CRM_1585579683', 'ITU List IV (Год выпуска)', 'date'),
      п('UF_CRM_1585579715', 'ITU List V (Год выпуска)', 'date'),
      п('UF_CRM_1585579747',
        'Manual for Use by the Maritime Mobile and Maritime Mobile-Satellite Services'
        + ' (Год выпуска)', 'date'),
      п('UF_CRM_1647927980', 'Оператор спутниковой связи (при наличии)', 'string'),
      п('UF_CRM_1647945060', 'Дата выдачи COC/APT VDR', 'date'),
      п('UF_CRM_1659349551', 'Формы проверок 4.1.1.', 'file', { multiple: true }),
      п('UF_CRM_1685447136', 'Формы проверок 4.1.2.', 'file', { multiple: true }),
      п('UF_CRM_1685447197', 'Формы проверок 4.1.6.', 'file', { multiple: true }),
      п('UF_CRM_1685447258', 'Формы проверок РР 1.1.', 'file', { multiple: true }),
      п('UF_CRM_1725626313', 'Инженер, выполнивший верификацию', 'employee'),
    ],
  },
  {
    key: 'дополнительно',
    title: 'дополнительно',
    поля: [
      п('UF_CRM_1552290385357', 'Название судна на английском', 'string'),
      п('UF_CRM_1552383324552', 'Флаг судна', 'string'),
      п('UF_CRM_1552383346054', 'Порт приписки', 'string'),
      п('UF_CRM_1553243734811', 'Позывной сигнал', 'string'),
      п('UF_CRM_1604052448136', 'Проект судна', 'string'),
      п('UF_CRM_1508506371', 'Marinetraffic', 'url'),
      п('UF_CRM_1508506533', 'fleetphoto', 'url'),
      п('UF_CRM_1508506569', 'rs-head', 'url'),
    ],
  },
  {
    key: 'документы-бто',
    title: 'Документы БТО',
    поля: [
      п('UF_CRM_1585919814', 'Ship Station Licence/ Радиолицензия', 'file', { multiple: true }),
      п('UF_CRM_1585920024', 'Shore-based Maintenance Agreement', 'file', { multiple: true }),
      п('UF_CRM_1585920064', 'EPIRB Annual Test Certificate', 'file', { multiple: true }),
      п('UF_CRM_1585920093', 'EPIRB Shore Based Maintenance Certificate (5 Years)', 'file',
        { multiple: true }),
      п('UF_CRM_1584365593403', 'SART Certificates (копии)', 'file', { multiple: true }),
      п('UF_CRM_1585920128', 'SSAS Test Report', 'file', { multiple: true }),
      п('UF_CRM_1585920151', 'LRIT Conformance Test Report', 'file', { multiple: true }),
      п('UF_CRM_1585920187', 'AIS Test Report', 'file', { multiple: true }),
      п('UF_CRM_1585920226', 'VDR Test Report', 'file', { multiple: true }),
      п('UF_CRM_1592828287', 'Акт проверок ГМССБ 6 3 22', 'file'),
      п('UF_CRM_1626339505955', 'Акты проверок', 'file', { multiple: true }),
      п('UF_CRM_1584364704043', 'Shore-based Maintenance Agreement (копия)', 'file'),
    ],
  },
]

/**
 * Первый раздел макета контакта — «О контакте»: он остаётся всегда. Остальные разделы
 * на карточке ЧЕЛОВЕКА прячутся, когда в них нет ни одного значения. Правило проверено
 * по данным зеркала (31.07.2026): поля верификаций и документов БТО заполнены у судов
 * (сотни–тысячи записей), у людей — единичные остатки (0–42 из 14 114), и прятать их,
 * когда они пусты, честно; когда в них что-то ЕСТЬ — раздел показывается и у человека,
 * данные важнее правила. На карточке СУДНА разделы видны всегда.
 */
export const РАЗДЕЛ_ВСЕГДА = 'о-контакте'

// ─────────────────────────── пустота по-битриксовски ───────────────────────────

/** У этих типов значение — ссылка на запись, и '0' означает «нет связи», а не запись №0. */
const ССЫЛОЧНЫЕ: ReadonlySet<string> = new Set(['crm', 'employee', 'user'])

/**
 * Пусто ли значение Bitrix. Пустоту Bitrix пишет пятью способами: null, '', false
 * (незаполненный файл/строка), [] (множественное без значений) и '0' у ссылочных полей.
 */
export function пустоеЗначение(v: unknown, тип: string): boolean {
  if (v === null || v === undefined || v === '' || v === false) return true
  if (Array.isArray(v) && v.length === 0) return true
  if (ССЫЛОЧНЫЕ.has(тип) && (v === '0' || v === 0)) return true
  return false
}

/** Сколько полей раздела заполнено в записи (special-строки не считаются — они не данные). */
export function заполненоПолей(раздел: РазделМакета, запись: Record<string, unknown>): number {
  let n = 0
  for (const поле of раздел.поля) {
    if (поле.type === 'special') continue
    if (!пустоеЗначение(запись[поле.code], поле.type)) n += 1
  }
  return n
}

// ─────────────────────────── разбор значений ───────────────────────────

/** Деньги Bitrix: '515000|RUB' → {сумма: '515000', валюта: 'RUB'}. Мусор → null. */
export function деньгиBitrix(v: unknown): { сумма: string; валюта: string } | null {
  if (typeof v === 'number') return { сумма: String(v), валюта: 'RUB' }
  if (typeof v !== 'string' || !v) return null
  const [сумма, валюта = 'RUB'] = v.split('|')
  if (!сумма.trim()) return null
  return { сумма: сумма.trim(), валюта: (валюта.trim() || 'RUB').toUpperCase() }
}

/**
 * Ссылка crm-поля. Bitrix пишет её то голым числом ('18186'), то с приставкой сущности
 * ('CO_123' — компания, 'C_123' — контакт, 'D_123' — сделка; формы живые, см. cast_val
 * в core_api.py). Голое число получает вид из crmTarget поля — сам Bitrix хранит цель
 * в настройках поля, и она снята оттуда же (bx_fields.raw->'settings').
 */
export function ссылкаCrm(
  v: unknown, цель?: 'company' | 'contact' | 'deal',
): { kind: 'company' | 'contact' | 'deal' | null; id: string } | null {
  const s = String(v ?? '').trim()
  const м = /^(?:(CO|C|D|L)_)?(\d+)$/.exec(s)
  if (!м) return null
  const виды = { CO: 'company', C: 'contact', D: 'deal' } as const
  const поПриставке = м[1] ? (виды as Record<string, 'company' | 'contact' | 'deal'>)[м[1]] : undefined
  return { kind: поПриставке ?? цель ?? null, id: м[2] }
}

/** Один файл Bitrix, как он лежит в raw: {id, showUrl, downloadUrl}. */
interface ФайлBitrix { id?: unknown; showUrl?: unknown }

/** Файлы поля: объект или массив объектов → список. Пустота ([]/false/null) → []. */
export function файлыBitrix(v: unknown): ФайлBitrix[] {
  if (Array.isArray(v)) return v.filter((x): x is ФайлBitrix => !!x && typeof x === 'object')
  if (v && typeof v === 'object') return [v as ФайлBitrix]
  return []
}

// ─────────────────────── сырьё зеркала для раскладки ───────────────────────

export interface СырьёКарточки {
  /** Полная запись зеркала (raw Bitrix). null — зеркало отстало, записи нет. */
  запись: Record<string, unknown> | null
  /** Насколько зеркало отстало (минуты) — когда ядро ответило _stale вместо записи. */
  отсталоМин?: number
}

/**
 * Полная запись зеркала для карточки. Нужна ТОЛЬКО под источником 'own': своя база отдаёт
 * карточку без сырых UF-полей, а раскладка Bitrix почти целиком из них. Под 'mirror'
 * карточка и так пришла полной — хук в сеть не ходит (enabled=false), второй запрос
 * за тем же ответом был бы платой ни за что.
 *
 * Запись с адресом «own-…» номера Bitrix не имеет — спрашивать зеркало не о чем.
 */
export function useСырьёКарточкиBitrix(
  метод: 'crm.company.get' | 'crm.contact.get', id: string | undefined,
) {
  const числовой = !!id && /^\d+$/.test(id)
  const нужен = числовой && источник() === 'own'
  const q = useQuery({
    queryKey: ['bx-раскладка', метод, id],
    enabled: нужен,
    staleTime: 60_000,
    retry: 1,
    queryFn: async (): Promise<СырьёКарточки> => {
      const res = await fetch('/api/bx', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ method: метод, params: { id } }),
      })
      if (res.status === 401) throw new Unauthorized()
      const текст = await res.text()
      const тело = (текст ? JSON.parse(текст) : {}) as {
        result?: Record<string, unknown>; _stale?: boolean; age_min?: number
        error?: string; detail?: string
      }
      if (!res.ok) throw new Error(тело.error || тело.detail || `HTTP ${res.status}`)
      if (тело._stale) return { запись: null, отсталоМин: тело.age_min }
      return { запись: тело.result ?? null }
    },
  })
  return { ...q, нужен, числовой }
}

/**
 * Запись для раскладки: сырьё зеркала + ответ своей базы ПОВЕРХ. Порядок не случаен:
 * своя база кое-что знает лучше зеркала (например, раздвинутые обратно имя/фамилия у
 * 2 955 контактов со сдвигом переноса — сверка 30.07.2026), поэтому её непустые значения
 * главнее. Пустые же
 * значения своей базы (null у DATE_MODIFY, [] у EMAIL судна) зеркало НЕ затирают: пустота
 * там означает «своя база этого не хранит», а не «в Bitrix пусто».
 */
export function слитьДляРаскладки(
  база: Record<string, unknown> | null | undefined,
  зеркало: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const из: Record<string, unknown> = { ...(зеркало ?? {}) }
  for (const [к, v] of Object.entries(база ?? {})) {
    if (v === null || v === undefined || v === '') continue
    if (Array.isArray(v) && v.length === 0) continue
    из[к] = v
  }
  return из
}

/**
 * Заметка под разделами: почему часть полей Bitrix могла не показаться. undefined — всё
 * пришло штатно. Три случая различаются, потому что и делать при них надо разное.
 */
export function заметкаСырья(
  х: { нужен: boolean; числовой: boolean; data?: СырьёКарточки; error?: unknown },
): string | undefined {
  if (!х.нужен) {
    // Зеркало не спрашивали: либо читаем его напрямую (src=mirror — поля уже полные),
    // либо у записи нет номера Bitrix и зеркального двойника не существует.
    return х.числовой || источник() === 'mirror'
      ? undefined
      : 'У записи нет номера Bitrix — это карточка только своей базы, зеркальных полей у неё нет.'
  }
  if (х.error) {
    return `Зеркало Bitrix не ответило (${х.error instanceof Error ? х.error.message : х.error}): `
      + 'показаны только поля своей базы.'
  }
  if (х.data && х.data.запись === null) {
    const мин = х.data.отсталоМин
    return `Зеркало Bitrix отстало${мин ? ` (~${мин} мин)` : ''} — ядро не отдаёт карточку; `
      + 'показаны только поля своей базы.'
  }
  return undefined
}
